"use client";

/**
 * DemoProvider — the orchestrator behind the click-through demo.
 *
 * Responsibilities:
 *  1. Hold demo state: { active, stepIndex }. Persisted to sessionStorage so a
 *     full page reload (or following a real link) survives.
 *  2. Expose `goNext` / `goPrev` / `goTo` / `start` / `exit` that:
 *       - Swap the signed-in user via POST /api/demo/become
 *       - Navigate to the step's `href`
 *  3. Render the persistent <DemoBar/> on every page when active.
 *
 * Mounted ONCE at the root layout. Most pages don't care it exists; only the
 * demo bar reads its context.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import {
  DEMO_STEPS, DEMO_ROLE_EMAIL, DEMO_ROLE_LABEL,
  type DemoStep, type DemoRole,
} from './demo-script';
import DemoBar from './DemoBar';
import { useAuth } from '@/contexts/AuthContext';
import { apiFetch } from '@/lib/api';

const STORAGE_KEY = 'imasterly:demo:v1';

type DemoState = {
  active: boolean;
  stepIndex: number;
};

type DemoContextValue = {
  active: boolean;
  stepIndex: number;
  step: DemoStep | null;
  totalSteps: number;
  start: () => Promise<void>;
  goTo: (index: number) => Promise<void>;
  goNext: () => Promise<void>;
  goPrev: () => Promise<void>;
  exit: () => Promise<void>;
  /** Like exit, but ALSO wipes tour-created data (DPS, Meera kids, Anjali
   *  school_teachers row) and sends the viewer to /admin/schools. Use this
   *  as the "Finish demo" CTA on the outro step. */
  finish: () => Promise<void>;
  navigating: boolean;
  roleLabel: string | null;
};

const DemoContext = createContext<DemoContextValue | null>(null);

function readPersisted(): DemoState {
  if (typeof window === 'undefined') return { active: false, stepIndex: 0 };
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return { active: false, stepIndex: 0 };
    const parsed = JSON.parse(raw);
    return {
      active: !!parsed.active,
      stepIndex: Number.isInteger(parsed.stepIndex) ? parsed.stepIndex : 0,
    };
  } catch {
    return { active: false, stepIndex: 0 };
  }
}

function persist(state: DemoState) {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch { /* quota / private-mode — non-blocking */ }
}

async function becomeRole(role: DemoRole): Promise<void> {
  console.log('  ⏳ becomeRole →', role);
  const t0 = performance.now();
  // role === 'none' means signed-out. Hit the logout endpoints (both auth
  // surfaces) so any prior session is fully cleared. This matters when the
  // viewer comes from a principal step → a logged-out landing-page step.
  if (role === 'none') {
    try { await apiFetch('/api/auth/logout',        { method: 'POST', credentials: 'include' }); } catch { /* ignore */ }
    try { await apiFetch('/api/school/auth/logout', { method: 'POST', credentials: 'include' }); } catch { /* ignore */ }
    console.log('  ✓ becomeRole signed out in', Math.round(performance.now() - t0), 'ms');
    return;
  }
  const email = DEMO_ROLE_EMAIL[role];
  if (!email) {
    console.warn('  becomeRole: no email mapping for role', role);
    return;
  }
  // /api/demo/become accepts the email + sets our session cookie. Backend
  // gates on NODE_ENV / DEMO_MODE_ALLOWED so this 404s in true production.
  try {
    const r = await apiFetch('/api/demo/become', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    if (!r.ok) {
      const body = await r.text();
      console.error('  becomeRole failed:', r.status, body);
    } else {
      console.log('  ✓ becomeRole signed in as', email, 'in', Math.round(performance.now() - t0), 'ms');
    }
  } catch (e) {
    console.error('  becomeRole fetch threw:', e);
  }
}

export function DemoProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const { checkAuth } = useAuth();
  const [state, setState] = useState<DemoState>({ active: false, stepIndex: 0 });
  const [hydrated, setHydrated] = useState(false);
  const [navigating, setNavigating] = useState(false);
  // While navigating to a step, the page may bounce through redirects
  // (signed-out viewers being kicked to /auth, school portal guards, etc.)
  // before Next.js settles on the real target. Hold the bar through that
  // turbulence by tracking the in-flight target pathname; auto-exit only
  // fires once pathname stabilizes AND doesn't match the active step.
  const inflightTargetRef = useRef<string | null>(null);

  // Hydrate from sessionStorage on first client render. Avoid SSR mismatch
  // by gating the bar render behind `hydrated`.
  // ALSO: on hydrate, if the demo is active and the current pathname matches
  // the active step's expected page, verify the live session matches the
  // step's expected role. If not (e.g. viewer reloaded a /admin/schools page
  // after their cookie drifted to a non-admin role), recast. This fixes the
  // "HTTP 403" scenario where the page loaded with a stale non-admin cookie.
  useEffect(() => {
    const persisted = readPersisted();
    setState(persisted);
    setHydrated(true);
    if (!persisted.active) return;
    const step = DEMO_STEPS[persisted.stepIndex];
    if (!step) return;
    const expectedPath = step.href.split('?')[0];
    if (window.location.pathname !== expectedPath && !window.location.pathname.startsWith(expectedPath + '/')) return;
    // Async, non-blocking — fire-and-forget. If the session is fine this is
    // a single GET; if a recast is needed we then refresh AuthContext + reload
    // the page so server-fetched data reflects the new identity.
    (async () => {
      try {
        const me = await apiFetch('/api/auth/me', { credentials: 'include' }).then(r => r.ok ? r.json() : null);
        const expectedEmail = step.role === 'none' ? null : DEMO_ROLE_EMAIL[step.role];
        const currentEmail = me?.user?.email ?? null;
        if (currentEmail === expectedEmail) return; // session matches, nothing to do
        console.log('[demo] hydrate: session', currentEmail, '!= expected', expectedEmail, '— recasting');
        await becomeRole(step.role);
        await checkAuth();
        // Reload so the page re-fetches /api/admin/schools etc. with the
        // freshly-set cookie. Without this, the React tree still shows the
        // 403 it captured on first mount.
        window.location.reload();
      } catch (e) {
        console.warn('[demo] hydrate recast failed:', e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist whenever it changes.
  useEffect(() => {
    if (hydrated) persist(state);
  }, [state, hydrated]);

  // Auto-exit USED to kill the bar when pathname didn't match the active
  // step's expected href — but in practice it fired on transient redirects
  // (e.g. /teacher/dashboard briefly bouncing through /connect on mount)
  // and stranded viewers mid-tour with no controls. Removed entirely. The
  // bar now persists until the viewer explicitly clicks Exit (small button
  // in the top-right corner, always visible during demo). If they navigate
  // off-script the bar follows them — they can Exit anytime.
  //
  // We still clear inflightTargetRef when pathname lands on the active
  // step's expected URL so future logic (if any) knows the in-flight
  // navigation completed.
  useEffect(() => {
    if (!hydrated || !state.active || navigating) return;
    const target = DEMO_STEPS[state.stepIndex];
    if (!target) return;
    const expectedPath = target.href.split('?')[0];
    if (pathname === expectedPath || pathname.startsWith(expectedPath + '/')) {
      inflightTargetRef.current = null;
    }
  }, [pathname, hydrated, state.active, state.stepIndex, navigating]);

  const totalSteps = DEMO_STEPS.length;
  const step = state.active ? DEMO_STEPS[state.stepIndex] ?? null : null;

  const advanceTo = useCallback(async (index: number, opts?: { skipBeforeAdvance?: boolean }) => {
    const target = DEMO_STEPS[index];
    if (!target) {
      console.warn('[demo] advanceTo: no step at index', index);
      return;
    }
    console.group(`%c[demo] advance → step ${index + 1}/${DEMO_STEPS.length} · ${target.id}`, 'color:#E8484C;font-weight:bold');
    console.log('  from step', state.stepIndex + 1, DEMO_STEPS[state.stepIndex]?.id ?? '(none)');
    console.log('  target href', target.href);
    console.log('  target role', target.role);
    console.log('  skipBeforeAdvance?', !!opts?.skipBeforeAdvance);
    setNavigating(true);
    try {
      const current = DEMO_STEPS[state.stepIndex];
      if (!opts?.skipBeforeAdvance && current?.beforeAdvance) {
        console.log('  ⏳ running current step\'s beforeAdvance hook…');
        const t0 = performance.now();
        try { await current.beforeAdvance(); } catch (e) {
          console.error('  beforeAdvance threw (non-blocking):', e);
        }
        console.log('  ✓ beforeAdvance done in', Math.round(performance.now() - t0), 'ms');
      }
      // Per-step priming: some steps need server state wiped before the
      // viewer arrives so the flow is replayable. Today only Meera's arc
      // does this — reset her kids + re-arm pending_school_slug so the
      // dashboard's "auto-open modal on empty" guard fires naturally.
      // We run the reset on BOTH steps of Meera's arc so jumping straight
      // to the dashboard (via the chapter list) still works.
      if (target.id === 'parent-signs-up' || target.id === 'parent-dashboard') {
        console.log('  ⏳ resetting Meera (zero kids + pending sunrise)…');
        try { await apiFetch('/api/demo/reset-meera', { method: 'POST', credentials: 'include' }); } catch (e) {
          console.warn('  reset-meera fetch threw:', e);
        }
      }
      // Step 1 ("intro") just lands on the tour-index page with a "Signed
      // out" narrator badge — but the viewer (the admin who launched the
      // tour) shouldn't actually be signed out yet. Keep their session
      // intact; step 2's cast is the first real role swap.
      if (target.id !== 'intro') {
        await becomeRole(target.role);
      } else {
        console.log('  · skipping role swap on intro step (keep launcher session)');
      }
      console.log('  ⏳ refreshing AuthContext…');
      try { await checkAuth(); } catch (e) {
        console.warn('  checkAuth threw (non-blocking):', e);
      }
      await new Promise(r => setTimeout(r, 80));
      setState({ active: true, stepIndex: index });
      inflightTargetRef.current = target.href.split('?')[0];
      console.log('  → router.push', target.href);
      router.push(target.href);
      console.groupEnd();
    } finally {
      setTimeout(() => setNavigating(false), 220);
    }
  }, [router, checkAuth, state.stepIndex]);

  // Wipe any residue from a prior tour run (DPS, Meera kids, Anjali's
  // school_teachers row). Strictly scoped to demo-owned data — never touches
  // real users. Best-effort; non-blocking. Called from both start() AND
  // goTo() (Resume) so any tour entry-point lands in a clean world. If we
  // didn't run this on Resume, a half-finished tour from a previous viewer
  // could leave residue that breaks the next steps (e.g. Anjali's already-
  // active school_teachers row makes her step-7 signup fail).
  const wipeResidue = useCallback(async () => {
    try {
      await apiFetch('/api/demo/reset-all', { method: 'POST', credentials: 'include' });
    } catch (e) {
      console.warn('[demo] reset-all failed (non-blocking):', e);
    }
  }, []);

  const start = useCallback(async () => {
    await wipeResidue();
    await advanceTo(0);
  }, [advanceTo, wipeResidue]);

  const goTo = useCallback(async (index: number) => {
    if (index < 0 || index >= DEMO_STEPS.length) return;
    await wipeResidue();
    await advanceTo(index);
  }, [advanceTo, wipeResidue]);

  const goNext = useCallback(async () => {
    if (state.stepIndex < DEMO_STEPS.length - 1) {
      await advanceTo(state.stepIndex + 1);
    }
  }, [advanceTo, state.stepIndex]);

  const goPrev = useCallback(async () => {
    if (state.stepIndex > 0) {
      await advanceTo(state.stepIndex - 1, { skipBeforeAdvance: true });
    }
  }, [advanceTo, state.stepIndex]);

  const exit = useCallback(async () => {
    setNavigating(true);
    try {
      await becomeRole('none');
      // Clean up the demo-session cookie so future visitors without admin
      // credentials are bounced to /auth instead of inheriting our access.
      try {
        await apiFetch('/api/demo/exit', { method: 'POST', credentials: 'include' });
      } catch { /* non-blocking */ }
      setState({ active: false, stepIndex: 0 });
      if (typeof window !== 'undefined') {
        window.sessionStorage.removeItem(STORAGE_KEY);
      }
      // After exit, send the viewer back to the tour index (NOT '/demo' —
      // that route now 308s to homepage since the story-card teaser was removed).
      router.push('/demo/tour');
    } finally {
      setTimeout(() => setNavigating(false), 220);
    }
  }, [router]);

  // Big-CTA finish for the outro step. Wipes residue (DPS, Meera kids,
  // Anjali school_teachers row) BEFORE clearing the session cookie so the
  // demo-cookie auth path on /reset-all is still valid. Then lands on the
  // admin's real home page so they aren't dropped back at /demo/tour.
  const finish = useCallback(async () => {
    setNavigating(true);
    try {
      try {
        await fetch('/api/demo/reset-all', { method: 'POST', credentials: 'include' });
      } catch { /* non-blocking */ }
      await becomeRole('none');
      try {
        await fetch('/api/demo/exit', { method: 'POST', credentials: 'include' });
      } catch { /* non-blocking */ }
      setState({ active: false, stepIndex: 0 });
      if (typeof window !== 'undefined') {
        window.sessionStorage.removeItem(STORAGE_KEY);
      }
      // Land on the admin schools list — their real workspace.
      router.push('/admin/schools');
    } finally {
      setTimeout(() => setNavigating(false), 220);
    }
  }, [router]);

  const value = useMemo<DemoContextValue>(() => ({
    active: state.active,
    stepIndex: state.stepIndex,
    step,
    totalSteps,
    start,
    goTo,
    goNext,
    goPrev,
    exit,
    finish,
    navigating,
    roleLabel: step ? DEMO_ROLE_LABEL[step.role] : null,
  }), [state, step, totalSteps, start, goTo, goNext, goPrev, exit, finish, navigating]);

  return (
    <DemoContext.Provider value={value}>
      {children}
      {hydrated && state.active && (
        <>
          <DemoBar />
          <DemoExitChip onExit={exit} navigating={navigating} />
        </>
      )}
    </DemoContext.Provider>
  );
}

// Small floating top-right "Exit demo" chip. Always visible whenever a tour
// is active so the viewer can bail out from any page — including pages where
// the demo bar is hidden behind page chrome or the viewer has scrolled in a
// modal etc. Tiny on purpose; the main controls live on the DemoBar.
function DemoExitChip({ onExit, navigating }: { onExit: () => Promise<void>; navigating: boolean }) {
  return (
    <div className="fixed top-3 right-3 z-[101] pointer-events-none">
      <button
        type="button"
        onClick={() => onExit()}
        disabled={navigating}
        className="pointer-events-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-extrabold uppercase tracking-wider shadow-lg transition disabled:opacity-60 disabled:cursor-not-allowed hover:opacity-90 active:translate-y-px"
        style={{
          background: 'rgba(43,34,64,0.92)',
          color: '#fff',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          border: '1px solid rgba(255,255,255,0.12)',
          fontFamily: 'Baloo 2, Fredoka, cursive',
        }}
        aria-label="Exit demo mode"
        title="Exit demo mode"
      >
        ✕ Exit demo
      </button>
    </div>
  );
}

export function useDemo() {
  const ctx = useContext(DemoContext);
  if (!ctx) {
    throw new Error('useDemo must be used inside <DemoProvider/>');
  }
  return ctx;
}
